import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  FileText, Search, ChefHat, Eye,
  Loader2, BookOpen
} from 'lucide-react';
import axios from 'axios';
import { VitrineViewTab, VitrineEditorTab } from '../../components/ThekenbelegungTab';
import { BakenTab } from '../../components/BakenTab';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Kategorie-Badge ──────────────────────────────────────────────────────────
function KategorieBadge({ name }) {
  if (!name) return null;
  return (
    <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
      {name}
    </span>
  );
}

// ─── Handbuch Card ────────────────────────────────────────────────────────────
function HandbuchCard({ produkt, token, categoryName, onClick }) {
  const [thumbError, setThumbError] = useState(false);
  return (
    <Card
      className="overflow-hidden group cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
      data-testid={`handbuch-card-${produkt.id}`}
    >
      <div className="aspect-[4/3] bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center overflow-hidden relative">
        {produkt.thumbnail_url && !thumbError ? (
          <img
            src={`${API}${produkt.thumbnail_url}?token=${token}`}
            alt={produkt.name}
            className="w-full h-full object-contain"
            onError={() => setThumbError(true)}
          />
        ) : (
          <FileText className="h-12 w-12 text-amber-400/50" />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-90 transition-opacity drop-shadow" />
        </div>
      </div>
      <div className="p-3">
        <h3 className="font-semibold text-sm truncate">{produkt.name}</h3>
        {categoryName && <div className="mt-1"><KategorieBadge name={categoryName} /></div>}
        {produkt.beschreibung && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{produkt.beschreibung}</p>
        )}
      </div>
    </Card>
  );
}

// ─── Handbuch (Belegung) Tab ──────────────────────────────────────────────────
function HandbuchTab({ produkte, loading, token, categories }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCatId, setActiveCatId] = useState('');

  const catMap = {};
  categories.forEach((c) => { catMap[c.id] = c.name; });

  const filtered = produkte.filter((p) => {
    const matchesSearch = !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCat = !activeCatId || p.category_id === activeCatId;
    return matchesSearch && matchesCat;
  });

  const usedCatIds = new Set(produkte.map((p) => p.category_id).filter(Boolean));
  const visibleCats = categories.filter((c) => usedCatIds.has(c.id));

  return (
    <div className="space-y-4" data-testid="belegung-tab">
      {/* Kategorie-Filter Pills */}
      {visibleCats.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="kategorie-filter">
          <button
            onClick={() => setActiveCatId('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              activeCatId === ''
                ? 'bg-amber-500 text-white border-amber-500'
                : 'border-gray-300 dark:border-gray-600 text-muted-foreground hover:border-amber-400 hover:text-amber-600'
            }`}
            data-testid="kategorie-alle"
          >
            Alle
          </button>
          {visibleCats.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCatId(activeCatId === cat.id ? '' : cat.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                activeCatId === cat.id
                  ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
                  : 'border-gray-300 dark:border-gray-600 text-muted-foreground hover:border-amber-400 hover:text-amber-600'
              }`}
              data-testid={`kategorie-filter-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Suchfeld */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rezept suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="belegung-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="belegung-empty">
          <ChefHat className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">
            {searchTerm || activeCatId ? 'Kein Rezept gefunden' : 'Noch keine Rezepte verfügbar'}
          </p>
          {(searchTerm || activeCatId) && (
            <p className="text-sm mt-1">Versuchen Sie andere Filterkriterien.</p>
          )}
          {!searchTerm && !activeCatId && (
            <p className="text-sm mt-1">
              Admins können PDFs direkt bei den Produkten in der Backshop-Liste hinterlegen.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="handbuch-grid">
          {filtered.map((produkt) => (
            <HandbuchCard
              key={produkt.id}
              produkt={produkt}
              token={token}
              categoryName={catMap[produkt.category_id]}
              onClick={() => {
                if (produkt.pdf_url) {
                  window.open(`${API}${produkt.pdf_url}?token=${token}`, '_blank');
                }
              }}
            />
          ))}
        </div>
      )}

    </div>
  );
}

// ─── Verwaltungs-Hinweis (für Admin - als eigenes Panel statt separatem Tab) ──
// (entfernt – jetzt in BackshopListe integriert)

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BackshopBelegung() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const canWrite = ['admin', 'stationsleitung'].includes(user?.role);

  const [activeTab, setActiveTab] = useState('rezepte');
  const [produkte, setProdukte] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API}/api/backshop/handbuch`, { headers }),
        axios.get(`${API}/api/backshop/categories`, { headers }),
      ]);
      setProdukte(prodRes.data || []);
      setCategories(catRes.data || []);
    } catch (err) {
      console.error('[Handbuch] Fehler beim Laden:', err);
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Tabs: "Vitrine anlegen" + "Backen" nur für Admin/Stationsleitung sichtbar
  const TABS = [
    { id: 'rezepte', label: 'Rezepte' },
    { id: 'thekenbelegung', label: 'Thekenbelegung' },
    ...(canWrite ? [{ id: 'vitrine-anlegen', label: 'Vitrine anlegen' }] : []),
    { id: 'backen', label: 'Backen' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 md:p-6" data-testid="backshop-belegung-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="backshop-belegung-title">
            <BookOpen className="h-6 w-6 text-amber-500" />
            Handbuch
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Rezepte, Vitrine-Belegung und Konfigurator
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b dark:border-gray-700">
          <div className="flex gap-1 overflow-x-auto pb-px" data-testid="backshop-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab-Inhalte */}
        {activeTab === 'rezepte' && (
          <HandbuchTab produkte={produkte} loading={loading} token={token} categories={categories} />
        )}
        {activeTab === 'thekenbelegung' && (
          <VitrineViewTab user={user} />
        )}
        {activeTab === 'vitrine-anlegen' && canWrite && (
          <VitrineEditorTab />
        )}
        {activeTab === 'backen' && (
          <BakenTab />
        )}
      </div>
    </DashboardLayout>
  );
}
